import { useEffect, useState } from 'react';
import { clsx } from 'clsx';
import { XMarkIcon } from '@heroicons/react/24/outline';

import { LookupField } from '../../LookupField';
import type { LookupRecord } from '../../LookupField';
import { Info } from '../../Info/Info';
import { toast } from '../../Toast';
import { useMasterDocumentFormatters } from '../formatters';

import { useAdvancesTranslation } from './i18n';
import { useApplyAdvance } from './hooks';
import type { ApplyAdvanceModalProps, AdvancesHttpError } from './types';

/** Registro devuelto por la búsqueda server-side de saldos (Id en PascalCase). */
interface AdvanceBalanceLookupRecord extends LookupRecord {
  sourceDocumentReference?: string | null;
  availableAmount: number;
  currencyId: string;
}

/**
 * Modal para aplicar (reservar) un anticipo contra el documento.
 *
 * - Selección de saldo vía `LookupField` server-side (NFR-01; debounce interno del componente).
 * - Valida monto `> 0` y `≤ availableAmount` del saldo seleccionado (BV-AA-01/02).
 * - En error 422/409 el modal NO se cierra y muestra el mensaje (CA-F5); 403 → `Info` inline (FD-06).
 *
 * Sigue el patrón visual de los modales de MasterDocument (overlay + diálogo centrado).
 */
export function ApplyAdvanceModal({
  open,
  documentId,
  documentCurrencyId,
  applyAdvanceEndpoint,
  httpRequest,
  fetcher,
  searchEntity = 'available-advances',
  formatAmount: formatAmountProp,
  onClose,
}: ApplyAdvanceModalProps) {
  const { t } = useAdvancesTranslation();
  const { formatAmount: defaultFormatAmount } = useMasterDocumentFormatters();
  const formatAmount = formatAmountProp ?? defaultFormatAmount;
  const applyMutation = useApplyAdvance({ documentId, applyAdvanceEndpoint, httpRequest });

  const [selected, setSelected] = useState<AdvanceBalanceLookupRecord | null>(null);
  const [amount, setAmount] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);
  const [forbidden, setForbidden] = useState(false);

  // Reset al abrir
  useEffect(() => {
    if (open) {
      setSelected(null);
      setAmount('');
      setValidationError(null);
      setForbidden(false);
    }
  }, [open]);

  // Esc para cerrar
  useEffect(() => {
    if (!open) return;
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open, onClose]);

  if (!open) return null;

  const numericAmount = Number(amount);
  const available = selected?.availableAmount ?? 0;

  function validate(): string | null {
    if (!selected) return t('advances.error.selectBalance');
    if (!Number.isFinite(numericAmount) || numericAmount <= 0) {
      return t('advances.error.amountPositive');
    }
    if (numericAmount > available) return t('advances.error.amountExceeds');
    return null;
  }

  function handleConfirm() {
    const error = validate();
    setValidationError(error);
    if (error || !selected) return;

    setForbidden(false);
    applyMutation.mutate(
      {
        openBalanceId: selected.Id,
        appliedAmount: numericAmount,
        currencyId: selected.currencyId ?? documentCurrencyId,
        sourceDocumentReference: selected.sourceDocumentReference ?? null,
      },
      {
        onSuccess: () => {
          toast.success(t('advances.toast.applied'));
          onClose();
        },
        onError: (error) => {
          const status = (error as AdvancesHttpError)?.status;
          // 403 → Info inline; el modal permanece abierto (FD-06).
          if (status === 403) {
            setForbidden(true);
            return;
          }
          // 422/409/otros → toast; el modal permanece abierto para corregir (CA-F5).
          toast.error(error?.message || t('advances.error.generic'));
        },
      },
    );
  }

  const isApplying = applyMutation.isPending;

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 z-[300] bg-black/30 animate-[fadeIn_150ms_ease-out]"
        aria-hidden="true"
        onClick={onClose}
      />

      {/* Diálogo */}
      <div
        className={clsx(
          'fixed z-[301] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2',
          'w-[calc(100%-32px)] max-w-[460px]',
          'bg-bg-primary rounded-2xl shadow-lg',
          'animate-[fadeIn_150ms_ease-out]',
          'dark:bg-dark-bg-primary dark:shadow-none dark:border dark:border-dark-border-primary',
          'flex flex-col max-h-[90vh]',
        )}
        role="dialog"
        aria-modal="true"
        aria-labelledby="apply-advance-modal-title"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-5 pb-3 shrink-0">
          <h2
            id="apply-advance-modal-title"
            className="text-[15px] font-bold text-content-primary dark:text-dark-content-primary"
          >
            {t('advances.modal.title')}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className={clsx(
              'p-1.5 rounded-md transition-colors duration-150',
              'hover:bg-background-secondary dark:hover:bg-dark-bg-secondary',
              'text-content-secondary dark:text-dark-content-secondary',
              'focus:outline-hidden focus:ring-2 focus:ring-primary-400 focus:ring-offset-2',
              'dark:focus:ring-dark-border-custom dark:focus:ring-offset-dark-bg-primary',
            )}
            aria-label={t('advances.modal.cancel')}
          >
            <XMarkIcon className="w-4 h-4" aria-hidden="true" />
          </button>
        </div>

        {/* Body */}
        <div className="flex flex-col gap-4 px-5 pb-4 overflow-y-auto min-h-0">
          {/* Selección de saldo (LookupField server-side) */}
          <LookupField<AdvanceBalanceLookupRecord>
            entity={searchEntity}
            label={t('advances.modal.balanceLabel')}
            placeholder={t('advances.modal.balancePlaceholder')}
            fetcher={fetcher}
            displayFields={['sourceDocumentReference', 'availableAmount']}
            bindFields={['availableAmount', 'currencyId']}
            value={selected?.Id ?? null}
            onChange={(record) => {
              setSelected(record);
              setValidationError(null);
            }}
            required
          />

          {/* Disponible del saldo seleccionado */}
          {selected && (
            <p className="text-xs text-content-secondary dark:text-dark-content-secondary -mt-2">
              {t('advances.modal.availableLabel')}: {formatAmount(available)}
            </p>
          )}

          {/* Monto a aplicar */}
          <div className="flex flex-col gap-1.5">
            <label
              htmlFor="apply-advance-amount"
              className="text-sm font-medium text-content-primary dark:text-dark-content-primary"
            >
              {t('advances.modal.amountLabel')}
            </label>
            <input
              id="apply-advance-amount"
              type="number"
              inputMode="decimal"
              min={0}
              step="0.01"
              value={amount}
              onChange={(e) => {
                setAmount(e.target.value);
                setValidationError(null);
              }}
              className={clsx(
                'w-full px-3 py-2 text-sm rounded-lg',
                'border bg-bg-primary dark:bg-dark-bg-secondary',
                'text-content-primary dark:text-dark-content-primary',
                'placeholder:text-content-tertiary dark:placeholder:text-dark-content-secondary',
                'focus:outline-hidden focus:ring-2 focus:ring-primary-400 dark:focus:ring-dark-border-custom',
                validationError
                  ? 'border-red-400 dark:border-red-500'
                  : 'border-border-primary dark:border-dark-border-primary',
              )}
            />
          </div>

          {/* Error de validación / negocio */}
          {validationError && (
            <p className="text-xs text-red-600 dark:text-red-400" role="alert">
              {validationError}
            </p>
          )}

          {/* 403 sin permiso */}
          {forbidden && <Info color="red" title={t('advances.error.forbidden')} />}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-border-primary dark:border-dark-border-primary shrink-0">
          <button
            type="button"
            onClick={onClose}
            className={clsx(
              'px-4 py-2 rounded-md text-sm font-semibold',
              'border border-border-primary text-content-tertiary',
              'hover:bg-background-secondary transition-colors duration-150',
              'focus:outline-hidden focus:ring-2 focus:ring-primary-400 focus:ring-offset-2',
              'dark:border-dark-border-primary dark:text-dark-content-secondary',
              'dark:hover:bg-dark-bg-secondary',
              'dark:focus:ring-dark-border-custom dark:focus:ring-offset-dark-bg-primary',
            )}
          >
            {t('advances.modal.cancel')}
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={isApplying}
            className={clsx(
              'px-5 py-2 rounded-md text-sm font-semibold',
              'bg-primary-600 text-white',
              'hover:bg-primary-700 transition-colors duration-150',
              'focus:outline-hidden focus:ring-2 focus:ring-primary-400 focus:ring-offset-2',
              'dark:bg-dark-bg-inverse dark:text-dark-content-inverse dark:hover:bg-dark-bg-inverse/90',
              'dark:focus:ring-dark-border-custom dark:focus:ring-offset-dark-bg-primary',
              'disabled:opacity-40 disabled:pointer-events-none',
            )}
          >
            {isApplying ? t('advances.modal.applying') : t('advances.modal.confirm')}
          </button>
        </div>
      </div>
    </>
  );
}
